import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  LinearProgress,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Assessment,
  Download,
  ExpandMore,
  Refresh,
  Search,
  Visibility,
  Summarize,
  PictureAsPdf,
} from "@mui/icons-material";
import {
  EMPTY_FILTERS,
  countFormat,
  hoursFormat,
  validateFilters,
  exportHeaders,
  exportRow,
  SLA_HOURS,
} from "../model/indicadoresModel";
import IndicadoresCharts from "./IndicadoresCharts";
import ComplexStatisticsCard from "shared/components/Cards/StatisticsCards/ComplexStatisticsCard";

const fields = [
  ["ID", "ID"],
  ["Titulo", "Título"],
  ["Estado", "Estado"],
  ["Prioridad", "Prioridad"],
  ["Asignado_Grupo_Tecnicos", "Grupo técnico"],
  ["Categoria", "Categoría"],
  ["Fecha_Apertura", "Apertura"],
  ["Fecha_solucion", "Solución"],
  ["Estadisticas_tiempo_solucion_horas", "Tiempo solución"],
];
const percent = (value) =>
  value === null
    ? "Sin información"
    : `${value.toLocaleString("es-CO", { maximumFractionDigits: 1 })}%`;
const dateDisplay = (ticket, field) => {
  const time = field === "Fecha_Apertura" ? ticket.openingTime : ticket.solutionTime;
  return time === null
    ? "Sin información"
    : new Intl.DateTimeFormat("es-CO", {
        timeZone: "America/Bogota",
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(time));
};

function IndicatorFilters({ filters, groups, disabled, onApply }) {
  const [draft, setDraft] = useState(filters);
  useEffect(() => setDraft(filters), [filters]);
  const error = validateFilters(draft);
  const update = (field, value) => setDraft((previous) => ({ ...previous, [field]: value }));
  return (
    <Box
      component="form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!error) onApply(draft);
      }}
      sx={{
        p: 2.5,
        "& .MuiOutlinedInput-root": {
          minHeight: 56,
          fontSize: 15,
          color: "#254459",
          bgcolor: "#fff",
        },
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#9eafbf" },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "#426e8e",
        },
        "& .MuiInputLabel-root": { fontSize: 14, color: "#45657c" },
        "& .MuiFormHelperText-root": { fontSize: 12, color: "#61798d" },
        "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#61798d" },
        "& .MuiChip-root": { fontSize: 13, bgcolor: "#e6f0f8", color: "#254459" },
        "& .MuiButton-root": { minHeight: 56, fontSize: 13 },
        "& .MuiSelect-select": {
          display: "flex",
          alignItems: "center",
          minHeight: "56px !important",
          py: "0 !important",
        },
      }}
    >
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} sm={6} lg={3}>
          <TextField
            fullWidth
            type="date"
            label="Fecha inicio"
            InputLabelProps={{ shrink: true }}
            value={draft.fechaInicio}
            disabled={disabled}
            onChange={(event) => update("fechaInicio", event.target.value)}
            helperText="Desde las 00:00 · Colombia"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <TextField
            fullWidth
            type="date"
            label="Fecha fin"
            InputLabelProps={{ shrink: true }}
            value={draft.fechaFin}
            disabled={disabled}
            onChange={(event) => update("fechaFin", event.target.value)}
            helperText="Hasta las 23:59 · Colombia"
            error={Boolean(error)}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <Autocomplete
            multiple
            limitTags={2}
            options={groups}
            value={draft.grupos}
            disabled={disabled}
            onChange={(_, values) => update("grupos", values)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Grupos técnicos"
                placeholder={draft.grupos.length ? "" : "Todos los grupos"}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} lg={2}>
          <Stack direction="row" spacing={1}>
            <Button
              type="submit"
              variant="contained"
              sx={{ "&&": { color: "#fff", bgcolor: "#195b78" } }}
              disabled={disabled || Boolean(error)}
            >
              Aplicar
            </Button>
            <Button
              sx={{ "&&": { color: "#365b76" } }}
              disabled={disabled}
              onClick={() => {
                setDraft(EMPTY_FILTERS);
                onApply(EMPTY_FILTERS);
              }}
            >
              Limpiar
            </Button>
          </Stack>
        </Grid>
      </Grid>
      {error && (
        <Typography color="error" variant="caption" role="alert">
          {error}
        </Typography>
      )}
    </Box>
  );
}
IndicatorFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  groups: PropTypes.array.isRequired,
  disabled: PropTypes.bool.isRequired,
  onApply: PropTypes.func.isRequired,
};

function TicketTable({ page, query, loading, disabled, onQuery }) {
  const [search, setSearch] = useState(query.search);
  const [selected, setSelected] = useState(null);
  useEffect(() => setSearch(query.search), [query.search]);
  useEffect(() => {
    if (search === query.search || disabled) return undefined;
    const timer = setTimeout(() => onQuery({ ...query, search, page: 0 }), 300);
    return () => clearTimeout(timer);
  }, [search, query, onQuery, disabled]);
  return (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        gap={2}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="body2">
          {countFormat(page.total)} coincidencias · Búsqueda en todo el resultado filtrado
        </Typography>
        <TextField
          size="small"
          label="Buscar por cualquier campo"
          value={search}
          disabled={disabled}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{ startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} /> }}
          sx={{ width: { xs: "100%", sm: 340 } }}
        />
      </Stack>
      <Box sx={{ overflow: "hidden", minHeight: 4 }}>{loading && <LinearProgress />}</Box>
      <TableContainer sx={{ maxHeight: 620, opacity: loading ? 0.65 : 1 }}>
        <Table
          stickyHeader
          size="small"
          aria-label="Tickets de indicadores"
          sx={{
            "& th": {
              fontSize: 12,
              fontWeight: 700,
              bgcolor: "background.paper",
              whiteSpace: "nowrap",
            },
            "& td": { fontSize: 14, py: 1.5 },
          }}
        >
          <TableHead sx={{ display: "table-header-group" }}>
            <TableRow>
              {fields.map(([key, label]) => (
                <TableCell key={key} sortDirection={query.sort === key ? query.direction : false}>
                  <TableSortLabel
                    disabled={disabled || loading}
                    active={query.sort === key}
                    direction={query.sort === key ? query.direction : "asc"}
                    onClick={() =>
                      onQuery({
                        ...query,
                        page: 0,
                        sort: key,
                        direction: query.sort === key && query.direction === "asc" ? "desc" : "asc",
                      })
                    }
                  >
                    {label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell>Detalle</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {page.rows.map((ticket) => (
              <TableRow key={ticket.key} hover>
                {fields.map(([key]) => (
                  <TableCell key={key}>
                    {key === "Estado" ? (
                      <Chip
                        size="small"
                        label={ticket.Estado}
                        color={
                          ticket.Estado === "Resuelto"
                            ? "success"
                            : ticket.Estado === "Cerrado"
                            ? "info"
                            : "default"
                        }
                      />
                    ) : key.startsWith("Fecha_") ? (
                      dateDisplay(ticket, key)
                    ) : key === "Estadisticas_tiempo_solucion_horas" ? (
                      hoursFormat(ticket[key])
                    ) : key === "Titulo" ? (
                      <Tooltip title={ticket.Titulo}>
                        <Box
                          sx={{
                            width: 240,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {ticket.Titulo}
                        </Box>
                      </Tooltip>
                    ) : (
                      String(ticket[key])
                    )}
                  </TableCell>
                ))}
                <TableCell>
                  <Button
                    size="small"
                    aria-label={`Ver ticket ${ticket.ID}`}
                    disabled={loading}
                    onClick={() => setSelected(ticket)}
                  >
                    <Visibility fontSize="small" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!page.rows.length && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No se encontraron tickets para esta búsqueda.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={page.total}
        page={page.page}
        rowsPerPage={page.pageSize}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Filas por página"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${countFormat(count)}`}
        onPageChange={(_, value) => onQuery({ ...query, page: value })}
        onRowsPerPageChange={(event) =>
          onQuery({ ...query, page: 0, pageSize: Number(event.target.value) })
        }
        SelectProps={{ disabled: disabled || loading }}
        backIconButtonProps={{ disabled: disabled || loading || !page.page }}
        nextIconButtonProps={{
          disabled: disabled || loading || (page.page + 1) * page.pageSize >= page.total,
        }}
        sx={{ "& .MuiTablePagination-toolbar": { flexWrap: "wrap" } }}
      />
      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="md">
        <DialogTitle>Ticket {selected?.ID}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {selected &&
              exportRow(selected).map((value, index) => (
                <Grid
                  item
                  xs={12}
                  sm={index === 1 || index === 9 ? 12 : 6}
                  key={exportHeaders[index]}
                >
                  <Typography variant="caption" color="text.secondary">
                    {exportHeaders[index]}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
                  >
                    {String(value === "" ? "Sin información" : value)}
                  </Typography>
                </Grid>
              ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
TicketTable.propTypes = {
  page: PropTypes.object.isRequired,
  query: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  onQuery: PropTypes.func.isRequired,
};

export default function IndicadoresPanel({ state }) {
  const {
    data,
    filtros,
    query,
    loading,
    progress,
    cancelLoad,
    tableLoading,
    exporting,
    exportError,
    retryExport,
    error,
    handleFilter,
    handleQuery,
    refresh,
    exportFile,
  } = state;
  const [section, setSection] = useState("charts");
  const [exportAnchor, setExportAnchor] = useState(null);
  const busy = loading || exporting;
  const download = loading && progress?.phase === "download";
  const downloadPercent =
    download && progress.total ? Math.min(100, (100 * progress.loaded) / progress.total) : null;
  const megabytes = (bytes) =>
    `${(bytes / 1048576).toLocaleString("es-CO", { maximumFractionDigits: 1 })} MB`;
  const loadingMessage = exporting
    ? "Generando archivo…"
    : download
    ? progress.loaded
      ? `Descargando tickets · ${megabytes(progress.loaded)}${
          progress.total ? ` de ${megabytes(progress.total)}` : " recibidos"
        }`
      : "Esperando respuesta del servidor…"
    : progress?.phase === "filter"
    ? "Aplicando filtros…"
    : "Preparando indicadores y tabla…";
  const summary = data?.summary;
  const m = summary?.metrics;
  const cards = m
    ? [
        {
          title: "Total Tickets",
          count: countFormat(m.total),
          color: "dark",
          icon: "summarize",
          percentage: { color: "info", amount: countFormat(data.totalLoaded), label: "recibidos" },
        },
        {
          title: "En Curso",
          count: countFormat(summary.states["En curso"] || 0),
          color: "warning",
          icon: "pending_actions",
          percentage: {
            color: "warning",
            amount: percent(m.total ? (100 * (summary.states["En curso"] || 0)) / m.total : 0),
            label: "del total",
          },
        },
        {
          title: "Resueltos",
          count: countFormat(m.resolved),
          color: "success",
          icon: "check_circle",
          percentage: {
            color: "success",
            amount: percent(m.total ? (100 * m.resolved) / m.total : 0),
            label: "del total",
          },
        },
        {
          title: "Cerrados",
          count: countFormat(m.closed),
          color: "info",
          icon: "task_alt",
          percentage: {
            color: "info",
            amount: percent(m.total ? (100 * m.closed) / m.total : 0),
            label: "del total",
          },
        },
        {
          title: "Pendientes >3d",
          count: countFormat(m.chronic),
          color: "error",
          icon: "schedule",
          percentage: {
            color: "error",
            amount: percent(m.chronicPercent),
            label: "de los pendientes",
          },
        },
        {
          title: "Tiempo Promedio",
          count: hoursFormat(m.average),
          color: "secondary",
          icon: "access_time",
          percentage: { color: "warning", amount: countFormat(m.outside), label: "superan 24 h" },
        },
      ]
    : [];
  return (
    <Stack spacing={2.5} sx={{ minWidth: 0 }}>
      <Card sx={{ overflow: "hidden" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          gap={2}
          alignItems={{ sm: "center" }}
          sx={{ p: 2.5, borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: "healthyGreen.main",
                color: "#fff",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <Assessment />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Indicadores de tickets
              </Typography>
              <Typography variant="caption" color="text.secondary">
                GLPI · Volumen, seguimiento y tiempos de solución
              </Typography>
              {summary && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Actualizado:{" "}
                  {new Date(summary.calculatedAt).toLocaleString("es-CO", {
                    timeZone: "America/Bogota",
                  })}
                </Typography>
              )}
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              startIcon={<Refresh />}
              onClick={refresh}
              disabled={busy}
              variant="outlined"
              color="success"
              sx={{
                "&&": {
                  color: "#195b78",
                  borderColor: "#195b78",
                  bgcolor: "#eef5fa",
                  minHeight: 44,
                  fontWeight: 700,
                },
                "&&:hover": { bgcolor: "#dcebf5", borderColor: "#123f56" },
                "&&.Mui-disabled": { color: "#61798d", borderColor: "#becbd6", bgcolor: "#edf1f5" },
              }}
            >
              Actualizar
            </Button>
            <Button
              startIcon={<Download />}
              endIcon={<ExpandMore />}
              disabled={busy || tableLoading || !m?.total}
              variant="contained"
              color="success"
              sx={{
                color: "#fff",
                bgcolor: "healthyGreen.main",
                "&:hover": { bgcolor: "healthyGreen.focus" },
                minWidth: 180,
              }}
              onClick={(event) => setExportAnchor(event.currentTarget)}
            >
              {exporting ? "Generando reporte…" : "Exportar reporte"}
            </Button>
            <Menu
              anchorEl={exportAnchor}
              open={Boolean(exportAnchor)}
              onClose={() => setExportAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  width: 320,
                  maxWidth: "calc(100vw - 24px)",
                  mt: 1,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  p: 0.5,
                },
              }}
            >
              <MenuItem
                disabled={busy}
                sx={{ py: 1.5, gap: 1.5, borderRadius: 1.5 }}
                onClick={() => {
                  setExportAnchor(null);
                  exportFile("xlsx");
                }}
              >
                <Summarize sx={{ color: "success.main" }} />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Excel (Resumen)
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: "normal" }}
                  >
                    Indicadores, estados, grupos y evolución de los filtros aplicados.
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem
                disabled={busy || !m?.total}
                sx={{ py: 1.5, gap: 1.5, borderRadius: 1.5 }}
                onClick={() => {
                  setExportAnchor(null);
                  exportFile("pdf");
                }}
              >
                <PictureAsPdf sx={{ color: "#195b78" }} />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    PDF (Gráficas e indicadores)
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: "normal" }}
                  >
                    Gráficas, indicadores, estados, grupos técnicos, SLA y evolución de los filtros
                    aplicados.
                  </Typography>
                </Box>
              </MenuItem>
            </Menu>
          </Stack>
        </Stack>
        <IndicatorFilters
          filters={filtros}
          groups={data?.groups || []}
          disabled={busy || !data}
          onApply={handleFilter}
        />
        {busy && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            sx={{ px: 2.5, pb: 1.5 }}
          >
            <Typography variant="body2" color="text.secondary" role="status" aria-live="polite">
              {loadingMessage}
            </Typography>
            {download && cancelLoad && <Button onClick={cancelLoad}>Cancelar carga</Button>}
          </Stack>
        )}
        <Box sx={{ overflow: "hidden", height: 4 }}>
          {busy && (
            <LinearProgress
              aria-label={loadingMessage}
              variant={downloadPercent === null ? "indeterminate" : "determinate"}
              value={downloadPercent ?? undefined}
            />
          )}
        </Box>
      </Card>
      {error && (
        <Alert
          severity="error"
          action={
            <Button onClick={refresh} disabled={busy}>
              Reintentar
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      {exportError && (
        <Alert
          severity="error"
          action={
            <Button onClick={retryExport} disabled={busy}>
              Reintentar exportación
            </Button>
          }
        >
          {exportError}
        </Alert>
      )}
      {!summary && loading && (
        <Grid container spacing={2}>
          {Array.from({ length: 6 }, (_, index) => (
            <Grid item xs={12} sm={6} lg={4} key={index}>
              <Skeleton variant="rounded" height={115} />
            </Grid>
          ))}
        </Grid>
      )}
      {summary && (
        <>
          <Stack direction={{ xs: "column", sm: "row" }} gap={1} justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Fecha de apertura: {filtros.fechaInicio || "Sin límite inicial"} →{" "}
              {filtros.fechaFin || "Sin límite final"} · Colombia
            </Typography>
          </Stack>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              {cards.map((card) => (
                <Grid item xs={12} sm={6} md={4} xl={2} key={card.title}>
                  <ComplexStatisticsCard
                    title={card.title}
                    count={card.count}
                    color={card.color}
                    icon={card.icon}
                    percentage={card.percentage}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Los estados corresponden a la consulta actual. Pendientes antiguos:{" "}
            {countFormat(m.chronic)} sin resolver con más de 3 días al consultar. Referencia de
            solución: {SLA_HOURS} horas corridas; solo se evalúan resueltos/cerrados con tiempo
            válido.
          </Typography>
          {(m.missingOpening > 0 || m.missingSolution > 0) && (
            <Alert severity="info">
              Sin fecha válida: {countFormat(m.missingOpening)} de apertura y{" "}
              {countFormat(m.missingSolution)} de solución en resueltos/cerrados. Se excluyen del
              análisis de esa fecha.
            </Alert>
          )}
          {!m.total && (
            <Alert severity="info">
              No se encontraron tickets con estos filtros. Puedes cambiar las fechas o limpiar la
              selección.
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary">
            El Excel y el PDF usan todos los tickets de las fechas y grupos aplicados. El PDF
            incluye gráficas y datos agregados. La búsqueda de la tabla solo cambia las filas
            visibles.
          </Typography>
          <Accordion
            disableGutters
            expanded={section === "charts"}
            onChange={(_, open) => setSection(open ? "charts" : "")}
            TransitionProps={{ unmountOnExit: true }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Gráficas y distribución</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {section === "charts" && (
                <IndicadoresCharts key={JSON.stringify(filtros)} summary={summary} />
              )}
            </AccordionDetails>
          </Accordion>
          <Accordion
            disableGutters
            expanded={section === "tickets"}
            onChange={(_, open) => setSection(open ? "tickets" : "")}
            TransitionProps={{ unmountOnExit: true }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Detalle de tickets · {countFormat(m.total)}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {section === "tickets" && (
                <TicketTable
                  page={data.page}
                  query={query}
                  loading={tableLoading}
                  disabled={busy}
                  onQuery={handleQuery}
                />
              )}
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Stack>
  );
}
IndicadoresPanel.propTypes = { state: PropTypes.object.isRequired };
